// Select Subject — auto-detect the main subject of an image.
//
// Photoshop's implementation is an ML model. We use a heuristic that produces
// a reasonable mask for typical "subject on a contrasting background" images:
//
//   1. Compute per-pixel edge strength via a 3x3 Sobel operator on luminance.
//   2. Compute a center-bias map (peaked at the image center, falls off
//      radially) — most photographic subjects are roughly centered.
//   3. Compute a difference-from-border map: each pixel's color distance from
//      the average border color. The "background" tends to be near the border.
//   4. Combine the three signals and threshold to produce a binary mask.
//   5. Morphologically close (dilate + erode) to fill small holes and remove
//      isolated speckle.
//
// The result is a Selection mask sized to (width, height). The mask is 0/255
// binary (no anti-aliasing); use Selection.feather() if a soft edge is wanted.
//
// Layer: 3 (Document Model). MUST remain DOM-free / browser-free.

import { Selection } from "./Selection";

export interface SubjectDetectorOptions {
  /** Edge sensitivity 0..1 (default 0.5). Higher = more pixels flagged as edge. */
  edgeSensitivity?: number;
  /** Center bias 0..1 (default 0.4). 0 = no positional bias. */
  centerBias?: number;
  /** Border-color bias 0..1 (default 0.5). 0 = ignore border similarity. */
  borderBias?: number;
  /** Morphological close radius in pixels (default 3). */
  closeRadius?: number;
}

/**
 * Detect the principal subject in an RGBA buffer and return a Selection mask.
 * Always produces a binary mask (0 or 255 per pixel).
 */
export function detectSubject(
  buffer: Uint8ClampedArray | Uint8Array,
  width: number,
  height: number,
  opts: SubjectDetectorOptions = {},
): Selection {
  const edgeSens = clamp01(opts.edgeSensitivity ?? 0.5);
  const centerBias = clamp01(opts.centerBias ?? 0.4);
  const borderBias = clamp01(opts.borderBias ?? 0.5);
  const closeRadius = Math.max(0, Math.round(opts.closeRadius ?? 3));

  if (width <= 0 || height <= 0) {
    return Selection.empty(Math.max(0, width), Math.max(0, height));
  }

  // Build luminance map.
  const lum = new Uint8Array(width * height);
  for (let i = 0; i < width * height; i++) {
    const o = i * 4;
    const r = buffer[o]!;
    const g = buffer[o + 1]!;
    const b = buffer[o + 2]!;
    const a = buffer[o + 3]!;
    if (a === 0) {
      lum[i] = 0;
    } else {
      lum[i] = (0.299 * r + 0.587 * g + 0.114 * b) | 0;
    }
  }

  // Sobel edge magnitude.
  const edges = sobelMagnitude(lum, width, height);

  // Center bias map.
  const cx = (width - 1) / 2;
  const cy = (height - 1) / 2;
  const maxR = Math.sqrt(cx * cx + cy * cy) || 1;

  // Average border color.
  const border = averageBorderColor(buffer, width, height);

  // Score = w1*edge + w2*center + w3*colorDist(border)
  // Then threshold relative to score range.
  const score = new Float32Array(width * height);
  let maxScore = 0;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = y * width + x;
      // Edge term: high near subject boundaries (subject vs background).
      const e = edges[i]! / 255;
      // Center term: 1 at exact center, 0 at corner.
      const dx = x - cx;
      const dy = y - cy;
      const r = Math.sqrt(dx * dx + dy * dy) / maxR;
      const cTerm = 1 - r;
      // Border-color difference term.
      const o = i * 4;
      const a = buffer[o + 3]!;
      let bTerm = 0;
      if (a > 0) {
        const dr = buffer[o]! - border.r;
        const dg = buffer[o + 1]! - border.g;
        const db = buffer[o + 2]! - border.b;
        const dist = Math.sqrt(dr * dr + dg * dg + db * db);
        bTerm = dist / Math.sqrt(3 * 255 * 255);
      }
      const s = edgeSens * e + centerBias * cTerm + borderBias * bTerm;
      score[i] = s;
      if (s > maxScore) maxScore = s;
    }
  }

  // Threshold at half the maximum; pixels above are inside the subject. Also
  // require pixels to have non-zero alpha (transparent pixels are never
  // subject).
  const T = maxScore * 0.45;
  const bin = new Uint8Array(width * height);
  for (let i = 0; i < bin.length; i++) {
    const a = buffer[i * 4 + 3]!;
    bin[i] = (a > 0 && score[i]! >= T) ? 255 : 0;
  }

  // Morphological close: dilate then erode with radius `closeRadius`.
  const closed = closeRadius > 0 ? morphClose(bin, width, height, closeRadius) : bin;
  return new Selection(width, height, closed);
}

// ---------------------------------------------------------------------------
// Helpers — pure functions, all O(width*height).
// ---------------------------------------------------------------------------

function clamp01(v: number): number {
  if (!Number.isFinite(v)) return 0;
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

function sobelMagnitude(lum: Uint8Array, w: number, h: number): Uint8Array {
  const out = new Uint8Array(w * h);
  if (w < 3 || h < 3) return out;
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const i = y * w + x;
      const a = lum[i - w - 1]!;
      const b = lum[i - w]!;
      const c = lum[i - w + 1]!;
      const d = lum[i - 1]!;
      const f = lum[i + 1]!;
      const g = lum[i + w - 1]!;
      const hv = lum[i + w]!;
      const j = lum[i + w + 1]!;
      const gx = -a + c - 2 * d + 2 * f - g + j;
      const gy = -a - 2 * b - c + g + 2 * hv + j;
      const m = Math.min(255, Math.sqrt(gx * gx + gy * gy) | 0);
      out[i] = m;
    }
  }
  return out;
}

function averageBorderColor(
  buf: Uint8ClampedArray | Uint8Array,
  w: number,
  h: number,
): { r: number; g: number; b: number } {
  let r = 0, g = 0, b = 0, n = 0;
  const sample = (x: number, y: number) => {
    const o = (y * w + x) * 4;
    const a = buf[o + 3]!;
    if (a > 0) {
      r += buf[o]!;
      g += buf[o + 1]!;
      b += buf[o + 2]!;
      n++;
    }
  };
  for (let x = 0; x < w; x++) {
    sample(x, 0);
    sample(x, h - 1);
  }
  for (let y = 1; y < h - 1; y++) {
    sample(0, y);
    sample(w - 1, y);
  }
  if (n === 0) return { r: 0, g: 0, b: 0 };
  return { r: r / n, g: g / n, b: b / n };
}

function morphClose(src: Uint8Array, w: number, h: number, r: number): Uint8Array {
  const dilated = morph(src, w, h, r, true);
  return morph(dilated, w, h, r, false);
}

function morph(src: Uint8Array, w: number, h: number, r: number, dilate: boolean): Uint8Array {
  const tmp = new Uint8Array(w * h);
  // Horizontal pass.
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let acc = dilate ? 0 : 255;
      const x0 = Math.max(0, x - r);
      const x1 = Math.min(w - 1, x + r);
      for (let k = x0; k <= x1; k++) {
        const v = src[y * w + k]!;
        if (dilate) { if (v > acc) acc = v; }
        else { if (v < acc) acc = v; }
      }
      tmp[y * w + x] = acc;
    }
  }
  const out = new Uint8Array(w * h);
  // Vertical pass.
  for (let x = 0; x < w; x++) {
    for (let y = 0; y < h; y++) {
      let acc = dilate ? 0 : 255;
      const y0 = Math.max(0, y - r);
      const y1 = Math.min(h - 1, y + r);
      for (let k = y0; k <= y1; k++) {
        const v = tmp[k * w + x]!;
        if (dilate) { if (v > acc) acc = v; }
        else { if (v < acc) acc = v; }
      }
      out[y * w + x] = acc;
    }
  }
  return out;
}
